import type { Maybe } from '@adornis/base/utilTypes.js';
import { useEffect, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import type { Person } from '../../users/entities/person.js';
import type { UserOnUserPayload } from '../../users/UserPolicy.js';
import type { UserWithPermissionFields } from '../UserWithPermissionFields.js';
import { canMartinusAbac } from './useMartinusAbac.js';

/**
 * Defines a page for a router or subrouter. Uses the ABAC system to determine if the page can be viewed from the currentUser.
 * Also provides support for a custom function which can be used to dynamically attach a page to a router or subrouter.
 * For example, the "children" page should be attached to every "parent" but is only accessible if the user also has the right permissions in the ABAC system.
 */
export interface AbacEnabledPage<U extends UserWithPermissionFields = UserWithPermissionFields> {
  /**
   * Routing parameters
   */
  label: string;
  path: string;

  /**
   * Custom function to determine if the page should be attached to a router or subrouter.
   * This is useful to dynamically attach a page to a router or subrouter based on the viewed user for example.
   * Defaults to true if not provided.
   */
  attached?: (viewedUser: U) => boolean;

  /**
   * The ABAC permission required to view this page. Automatically checked by the ABAC system.
   * Defaults to true if not provided. So if no permission is provided, the page will always be available.
   */
  permission?: string;
}

export function useAbacEnabledPages<U extends UserWithPermissionFields = UserWithPermissionFields>(
  params: { pages: AbacEnabledPage<U>[]; viewedUser: Maybe<U> },
  deps?: unknown[],
) {
  const user = useObservable(() => currentUser<Person>(), []);

  /**
   * No fallback to the current user: a viewed person that is still loading is nullish too, and evaluating the
   * pages against the logged-in account instead showed the wrong tabs and read fields (`hasLAK`) its selection
   * never requested. Callers that mean the logged-in account (the sidebar) pass it explicitly.
   */
  const viewedUser = params.viewedUser;

  /**
   * `undefined` until evaluated for the loaded users: detail pages gate their router outlet on this list, since a
   * pasted or reloaded URL reaches routes the tabs never offer, and an empty list would flash "no permission"
   */
  const [availablePages, setAvailablePages] = useState<Maybe<AbacEnabledPage<U>[]>>(undefined);

  useEffect(() => {
    const calculateAvailablePages = async () => {
      if (user === undefined || viewedUser === undefined) {
        setAvailablePages(undefined);
        return;
      }
      /**
       * No user -> no pages. Martinus requires a strict logged in status
       */
      if (!user || !viewedUser) {
        setAvailablePages([]);
        return;
      }

      const pages: AbacEnabledPage<U>[] = [];
      for (const value of Object.values(params.pages)) {
        /**
         * Check if the page should be attached to the router/subrouter
         * and if the user has the required permission
         * Both default to true if not given
         */
        if (
          (value.attached ? value.attached(viewedUser as U) : true) &&
          (value.permission
            ? await canMartinusAbac<UserOnUserPayload>({
                action: value.permission,
                currentUser: user,
                viewedUser: viewedUser as U,
              })
            : true)
        ) {
          pages.push(value);
        }
      }
      setAvailablePages(pages);
    };

    void calculateAvailablePages();
  }, [user, viewedUser, ...(deps ?? [])]);

  return availablePages;
}
